import * as React from 'react';
import { Info } from 'lucide-react';

import { cn } from '../../../lib/utils';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '../../ui/tooltip';

// ─── Constants ──────────────────────────────────────────────────────────────

const TOOLTIP_DELAY_MS = 100;

const VARIANT_CLASSES = {
  danger: 'bg-destructive/10 text-destructive',
  neutral: 'bg-primary/10 text-primary',
  success: 'bg-success/15 text-success',
  warning: 'bg-warning/20 text-warning-foreground',
} as const;

const VALUE_ACCENT_CLASSES = {
  destructive: 'text-destructive',
  success: 'text-success',
} as const;

// ─── Types ────────────────────────────────────────────────────────────────────

export type KpiCardVariant = keyof typeof VARIANT_CLASSES;
export type KpiCardValueAccent = keyof typeof VALUE_ACCENT_CLASSES;

export interface KpiCardProps {
  /** Icon rendered inside the tinted chip — pass a sized Lucide icon element. */
  icon: React.ReactNode;
  /** Translated uppercase label — i18n stays app-side, callers pass the resolved string. */
  label: string;
  /** Optional translated subtitle rendered below the value (e.g. an on-time/late breakdown). */
  subtitle?: string;
  /** Optional translated tooltip text shown via an info icon next to the label. */
  tooltip?: string;
  /** Optional accessible name for the tooltip trigger button — defaults to `label` when omitted. */
  tooltipAriaLabel?: string;
  value: number | string;
  /** Optional accent color applied to the value text. */
  valueAccent?: KpiCardValueAccent;
  /** Controls the icon chip's tint. */
  variant: KpiCardVariant;
}

// ─── Component ────────────────────────────────────────────────────────────────

/**
 * Bordered KPI card — a tinted icon chip next to an uppercase label (with an optional info
 * tooltip), a value (with an optional accent color), and an optional subtitle. Used by the
 * dashboard's Today KPIs row. Presentational only — no i18n, no data fetching.
 */
export function KpiCard({
  icon,
  label,
  subtitle,
  tooltip,
  tooltipAriaLabel,
  value,
  valueAccent,
  variant,
}: KpiCardProps): React.JSX.Element {
  return (
    <div className="rounded-lg border bg-card text-card-foreground shadow-sm">
      <div className="flex min-w-0 items-start gap-3 p-4">
        <div className={cn('mt-0.5 rounded-lg p-2', VARIANT_CLASSES[variant])}>{icon}</div>
        <div className="min-w-0 flex-1">
          <div className="flex items-center gap-1">
            <p className="truncate text-xs font-medium uppercase text-muted-foreground">{label}</p>
            {tooltip && (
              <TooltipProvider delayDuration={TOOLTIP_DELAY_MS}>
                <Tooltip>
                  <TooltipTrigger asChild>
                    <button
                      aria-label={tooltipAriaLabel ?? label}
                      className="inline-flex items-center justify-center rounded-full border-0 bg-transparent p-0 leading-none focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-1"
                      type="button"
                    >
                      <Info className="h-3.5 w-3.5 cursor-pointer text-foreground/70 hover:text-foreground" />
                    </button>
                  </TooltipTrigger>
                  <TooltipContent>
                    <p>{tooltip}</p>
                  </TooltipContent>
                </Tooltip>
              </TooltipProvider>
            )}
          </div>
          <p className={cn('mt-1 text-2xl font-bold leading-none', valueAccent && VALUE_ACCENT_CLASSES[valueAccent])}>
            {value}
          </p>
          {subtitle && <p className="mt-1.5 text-xs text-muted-foreground">{subtitle}</p>}
        </div>
      </div>
    </div>
  );
}
